import { flow, types, Instance, getSnapshot, applySnapshot, SnapshotIn } from 'mobx-state-tree';
import {
  fetchMe,
  fetchUsers,
  getSingleUser,
  createSingleUser,
  updateSingleUser,
  deleteSingleUser,
  changeUserPassword,
  uploadUsersFromFile,
  fetchCommunities,
  getSingleCommunity,
  createSingleCommunity,
  updateSingleCommunity,
  deleteSingleCommunity,
  login,
  logout,
  resetUserPassword,
  forgotUserPassword,
  fetchPortalUserStats,
} from '../services/api';
import {
  getUserStripeSubscriptions,
  getSubscriptionProducts,
  forceSubscriptionExpiry as forceSubscriptionExpiryRequest,
} from '../services/subscriptionApi';
import { readStorage, writeStorage, deleteStorage } from '../utils/storage';
import { composeError } from '../utils/transforms';
import { withRootStore } from './withRootStore';
import {
  Community,
  ForceSubscriptionExpiryPayload,
  LoginResponse,
  PortalUserFilters,
  PortalUserStats,
  PortalUsersResponse,
  StripeSubscription,
  Subscription,
  User,
  PORTAL_SUBSCRIPTION_PLATFORMS,
} from '../types';
import { UserRole } from '../utils/constants';
import { getUserListRole } from 'utils/userUtils';

const userRoleEnumValues = Object.values(UserRole) as string[];
let portalLocalPagingWarned = false;

export const CommunityModel = types.model({
  id: types.number,
  community: types.optional(types.string, ''),
  name: types.optional(types.string, ''),
});

export const SubscriptionTypeModel = types.model({
  id: types.number,
  name: types.string,
  active: types.boolean,
  type: types.number,
  description: types.maybeNull(types.string),
  price: types.number,
  priceYear: types.number,
});

export const SubscriptionModel = types.model({
  id: types.number,
  valid: types.boolean,
  periodStarts: types.string,
  periodEnds: types.maybeNull(types.string),
  name: types.optional(types.maybeNull(types.string), null),
  ownerId: types.maybeNull(types.number),
  subscriptionCode: types.maybeNull(types.string),
  subscriptionType: types.maybeNull(SubscriptionTypeModel),
  paymentType: types.maybeNull(types.number),
  supportCode: types.maybeNull(types.string),
  discountCode: types.maybeNull(types.string),
  platform: types.maybeNull(types.string),
  platformSubscriptionId: types.maybeNull(types.string),
  stripeSubscriptionId: types.maybeNull(types.string),
  autoRenews: types.maybeNull(types.boolean),
  status: types.optional(types.maybeNull(types.string), null),
  state: types.optional(types.maybeNull(types.string), null),
});

export const SubscriptionStripeModel = types.model({
  id: types.string,
  status: types.string,
  currentPeriodStart: types.optional(types.string, ''),
  currentPeriodEnd: types.optional(types.string, ''),
  interval: types.optional(types.string, ''),
  amount: types.maybeNull(types.number),
  subscriptionCode: types.maybeNull(types.string),
  subscriptionId: types.maybeNull(types.string),
  priceId: types.maybeNull(types.string),
  productId: types.maybeNull(types.string),
  paymentType: types.maybeNull(types.number),
  supportCode: types.maybeNull(types.string),
  discountCode: types.maybeNull(types.string),
});

const PortalFilterModel = types.model('PortalFilterModel', {
  platform: types.optional(types.array(types.enumeration('PortalFilterPlatform', PORTAL_SUBSCRIPTION_PLATFORMS)), []),
  stripeKind: types.optional(types.string, ''),
  codeOnly: types.optional(types.boolean, false),
  activeOnly: types.optional(types.boolean, false),
  missingEndDate: types.optional(types.boolean, false),
  marketing: types.optional(types.boolean, false),
  search: types.optional(types.string, ''),
  limit: types.optional(types.number, 100),
  offset: types.optional(types.number, 0),
});

const PortalStatsModel = types.model('PortalStatsModel', {
  totalUsers: types.number,
  activeSubscriptions: types.number,
  appleSubscriptions: types.number,
  googleSubscriptions: types.number,
  stripeSubscriptions: types.number,
  codeSubscriptions: types.number,
  adminSubscriptions: types.number,
  adminAddedSubscriptions: types.number,
});

const PortalPaginationModel = types.model('PortalPaginationModel', {
  total: types.number,
  limit: types.number,
  offset: types.number,
  hasMore: types.boolean,
});

export const UserModel = types.model('UserModel', {
  id: types.number,
  email: types.string,
  firstname: types.string,
  lastname: types.string,
  churchId: types.optional(types.maybeNull(types.string), null),
  address: types.optional(types.string, ''),
  postalcode: types.optional(types.string, ''),
  city: types.optional(types.string, ''),
  country: types.optional(types.string, ''),
  phone: types.optional(types.string, ''),
  admin: types.maybeNull(types.boolean),
  publisher: types.maybeNull(types.boolean),
  creator: types.maybeNull(types.boolean),
  employee: types.maybeNull(types.boolean),
  portal: types.maybeNull(types.boolean),
  communities: types.optional(types.array(CommunityModel), []),
  subscriptions: types.optional(types.array(SubscriptionModel), []),
  newsletter: types.maybeNull(types.boolean),
  empNewsletter: types.maybeNull(types.boolean),
  permission: types.maybeNull(types.boolean),
  marketing: types.maybeNull(types.boolean),
  subscriber: types.maybeNull(types.boolean),
  tester: types.boolean,
  customerId: types.maybeNull(types.string),
  emailVerified: types.maybeNull(types.boolean),
  communityIds: types.optional(types.array(types.number), []),
  subscriptionTypeId: types.maybeNull(types.number),
  subscriptionMonths: types.maybeNull(types.number),
  deletedAt: types.maybeNull(types.string),
  nextUsageResetDate: types.maybeNull(types.string),

  // Portal users (backend computed fields)
  activeSubscriptionPlatform: types.optional(types.maybeNull(types.string), null),
  activeSubscriptionPaymentType: types.optional(types.maybeNull(types.number), null),
  activeSubscriptionTypeName: types.optional(types.maybeNull(types.string), null),
  activeSubscriptionId: types.optional(types.maybeNull(types.number), null),
  latestSubscriptionPlatform: types.optional(types.maybeNull(types.string), null),
  latestSubscriptionPaymentType: types.optional(types.maybeNull(types.number), null),
  latestSubscriptionTypeName: types.optional(types.maybeNull(types.string), null),
  latestSubscriptionId: types.optional(types.maybeNull(types.number), null),
});

export type UserModelSnapshotIn = SnapshotIn<typeof UserModel>;

const ProductModel = types.model('ProductModel', {
  id: types.string,
  name: types.string,
  description: types.maybeNull(types.string),
  price_month: types.number,
  price_month_id: types.string,
  price_year: types.number,
  price_year_id: types.string,
  active: types.boolean,
});

export type ProductModelType = Instance<typeof ProductModel>;

const defaultPortalFilters: PortalUserFilters = {
  platform: [],
  stripeKind: '',
  codeOnly: false,
  activeOnly: false,
  missingEndDate: false,
  marketing: false,
  search: '',
  limit: 100,
  offset: 0,
};

const defaultPortalStats: PortalUserStats = {
  totalUsers: 0,
  activeSubscriptions: 0,
  appleSubscriptions: 0,
  googleSubscriptions: 0,
  stripeSubscriptions: 0,
  codeSubscriptions: 0,
  adminSubscriptions: 0,
  adminAddedSubscriptions: 0,
};

const defaultPortalPagination = {
  total: 0,
  limit: defaultPortalFilters.limit ?? 200,
  offset: 0,
  hasMore: false,
};

export const UserStore = types
  .model({
    state: types.maybeNull(types.enumeration(['LOGGED_OUT', 'LOGGED_IN', 'IN_PROGRESS', 'ERROR'])),
    token: types.optional(types.maybeNull(types.string), readStorage('AUTH_TOKEN')),
    user: types.frozen(),
    error: types.maybeNull(types.frozen()),
    data: types.optional(types.array(UserModel), []),
    users: types.optional(types.array(UserModel), []),
    singleUser: types.maybeNull(UserModel),
    communities: types.optional(types.array(CommunityModel), []),
    stripeSubscriptions: types.optional(types.array(SubscriptionStripeModel), []),
    stripeSubscriptionsUserId: types.maybeNull(types.string),
    stripeSubscriptionsLoading: types.optional(types.boolean, false),
    stripeSubscriptionsRequestToken: types.optional(types.number, 0),
    products: types.optional(types.array(ProductModel), []),
    portalFilters: types.optional(PortalFilterModel, defaultPortalFilters),
    portalStats: types.optional(PortalStatsModel, defaultPortalStats),
    portalPagination: types.optional(PortalPaginationModel, defaultPortalPagination),
    portalUsersLoading: types.optional(types.boolean, false),
    portalStatsLoading: types.optional(types.boolean, false),
    latestUsersRole: types.optional(types.maybeNull(types.enumeration('UserStoreRole', userRoleEnumValues)), null),
    usersRequestToken: types.optional(types.number, 0),
  })
  .extend(withRootStore)
  .actions((self) => {
    const coerceNumber = (value: unknown): number | undefined => {
      if (typeof value === 'number' && Number.isFinite(value)) return value;
      if (typeof value === 'string' && value.trim().length > 0) {
        const parsed = Number(value);
        if (Number.isFinite(parsed)) return parsed;
      }
      return undefined;
    };

    const coerceBoolean = (value: unknown): boolean | undefined => {
      if (typeof value === 'boolean') return value;
      if (typeof value === 'number') {
        if (value === 1) return true;
        if (value === 0) return false;
      }
      if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase();
        if (normalized === 'true') return true;
        if (normalized === 'false') return false;
      }
      return undefined;
    };

    const userLogin = flow(function* ({ email, password }) {
      try {
        const response: LoginResponse | undefined = yield login(email, password);
        if (response?.token) {
          self.token = response.token;
          self.user = {
            ...response.user,
            communities: response.user.communities?.map((r: Community) => ({ ...r, name: r.community })),
          };

          self.state = 'LOGGED_IN';
          writeStorage('AUTH_TOKEN', response.token);
        } else {
          const error = composeError(response);
          self.rootStore.errorStore.setError(error.message);
        }
      } catch (err: unknown) {
        const error = composeError(err);
        self.rootStore.errorStore.setError(error.message);
      }
    });

    const userLogout = flow(function* () {
      try {
        yield logout();
        self.user = null;
        self.token = null;
        self.state = 'LOGGED_OUT';
        deleteStorage('AUTH_TOKEN');
      } catch (err) {
        const error = composeError(err);
        self.rootStore.errorStore.setError(error.message);
      }
    });

    const getMe = flow(function* () {
      self.state = 'IN_PROGRESS';
      try {
        const response = yield fetchMe();
        self.user = {
          ...response.data,
          communities: response.data.communities?.map((r: Community) => ({ ...r, name: r.community })),
        };
        self.state = 'LOGGED_IN';
      } catch (err) {
        const error = composeError(err);
        self.rootStore.errorStore.setError(error.message);
        self.state = 'LOGGED_OUT';
        self.user = null;
        self.token = null;
        deleteStorage('AUTH_TOKEN');
      }
    });

    const mapCommunities = (communities?: Community[]) =>
      communities?.map((c: Community) => ({
        ...c,
        id: typeof c.id === 'number' ? c.id : 0,
        name: c.community,
      })) ?? [];

    const mapSubscriptionsWithName = (subscriptions?: Subscription[]) =>
      subscriptions?.map((s: Subscription) => {
        const name = s.subscriptionType?.name || s.name;
        return {
          ...s,
          // Avoid empty strings so UI can fall back to "no active subscription"
          name: name && name.trim() ? name : undefined,
        };
      }) ?? [];

    const mapSingleUserSnapshot = (user: User): UserModelSnapshotIn => ({
      id: user.id ?? 0,
      firstname: user.firstname,
      lastname: user.lastname,
      email: user.email,
      churchId: (user.churchId ?? null) as string | null,
      address: user.address ?? '',
      postalcode: user.postalcode ?? '',
      city: user.city ?? '',
      country: user.country ?? '',
      phone: user.phone ?? '',
      admin: user.admin,
      publisher: user.publisher,
      creator: user.creator,
      employee: user.employee,
      portal: !!user.portal,
      newsletter: user.newsletter ?? null,
      empNewsletter: user.empNewsletter ?? null,
      permission: user.permission ?? null,
      marketing: !!user.marketing,
      subscriber: user.subscriber,
      tester: user.tester ?? false,
      customerId: user.customerId ?? null,
      emailVerified: user.emailVerified ?? null,
      communities: mapCommunities(user.communities),
      subscriptions: mapSubscriptionsWithName(user.subscriptions),
      communityIds: user.communityIds ?? [],
      subscriptionTypeId: user.subscriptionTypeId ?? null,
      subscriptionMonths: user.subscriptionMonths ?? null,
      deletedAt: user.deletedAt ?? null,
      nextUsageResetDate: user.nextUsageResetDate ?? null,

      activeSubscriptionPlatform: user.activeSubscriptionPlatform ?? null,
      activeSubscriptionPaymentType: user.activeSubscriptionPaymentType ?? null,
      activeSubscriptionTypeName: user.activeSubscriptionTypeName ?? null,
      activeSubscriptionId: user.activeSubscriptionId ?? null,
      latestSubscriptionPlatform: user.latestSubscriptionPlatform ?? null,
      latestSubscriptionPaymentType: user.latestSubscriptionPaymentType ?? null,
      latestSubscriptionTypeName: user.latestSubscriptionTypeName ?? null,
      latestSubscriptionId: user.latestSubscriptionId ?? null,
    });

    const mapUserResponse = (r: User) => ({
      id: r.id ?? 0,
      firstname: r.firstname,
      lastname: r.lastname,
      email: r.email,
      address: r.address,
      postalcode: r.postalcode,
      city: r.city,
      country: r.country,
      phone: r.phone,
      admin: r.admin,
      publisher: r.publisher,
      creator: r.creator,
      employee: r.employee,
      portal: r.portal ?? false,
      newsletter: r.newsletter,
      empNewsletter: r.empNewsletter,
      permission: r.permission,
      marketing: r.marketing ?? false,
      subscriber: r.subscriber,
      tester: r.tester === null ? false : r.tester,
      nextUsageResetDate: r.nextUsageResetDate,
      communities: mapCommunities(r.communities),
      subscriptions: mapSubscriptionsWithName(r.subscriptions),

      activeSubscriptionPlatform: r.activeSubscriptionPlatform ?? null,
      activeSubscriptionPaymentType: r.activeSubscriptionPaymentType ?? null,
      activeSubscriptionTypeName: r.activeSubscriptionTypeName ?? null,
      activeSubscriptionId: r.activeSubscriptionId ?? null,
      latestSubscriptionPlatform: r.latestSubscriptionPlatform ?? null,
      latestSubscriptionPaymentType: r.latestSubscriptionPaymentType ?? null,
      latestSubscriptionTypeName: r.latestSubscriptionTypeName ?? null,
      latestSubscriptionId: r.latestSubscriptionId ?? null,
    });

    const shouldResetPortalOffset = (updates: Partial<PortalUserFilters>) =>
      updates.search !== undefined ||
      updates.platform !== undefined ||
      updates.stripeKind !== undefined ||
      updates.codeOnly !== undefined ||
      updates.activeOnly !== undefined ||
      updates.missingEndDate !== undefined ||
      updates.marketing !== undefined ||
      updates.limit !== undefined;

    const setPortalFilters = (updates: Partial<PortalUserFilters> = {}) => {
      if (typeof updates.codeOnly === 'boolean') {
        self.portalFilters.codeOnly = updates.codeOnly;
      }
      const codeOnlyEnabled = self.portalFilters.codeOnly === true;
      if (updates.platform !== undefined && !codeOnlyEnabled) {
        self.portalFilters.platform.replace(updates.platform ?? []);
      }
      if (updates.stripeKind !== undefined && !codeOnlyEnabled) {
        self.portalFilters.stripeKind = updates.stripeKind ?? '';
      }
      if (codeOnlyEnabled) {
        self.portalFilters.platform.replace([]);
        self.portalFilters.stripeKind = '';
      }
      if (typeof updates.activeOnly === 'boolean') {
        self.portalFilters.activeOnly = updates.activeOnly;
      }
      if (typeof updates.missingEndDate === 'boolean') {
        self.portalFilters.missingEndDate = updates.missingEndDate;
      }
      if (typeof updates.marketing === 'boolean') {
        self.portalFilters.marketing = updates.marketing;
      }
      if (updates.search !== undefined) {
        self.portalFilters.search = updates.search ?? '';
      }
      if (typeof updates.limit === 'number') {
        self.portalFilters.limit = updates.limit;
      }
      if (typeof updates.offset === 'number') {
        self.portalFilters.offset = updates.offset;
      } else if (shouldResetPortalOffset(updates)) {
        self.portalFilters.offset = 0;
      }
    };

    const resetPortalFilters = flow(function* () {
      applySnapshot(self.portalFilters, defaultPortalFilters);
      yield getUsersByRole(UserRole.portal);
    });

    const loadPortalUsersWithFilters = flow(function* (updates?: Partial<PortalUserFilters>) {
      if (updates) {
        setPortalFilters(updates);
      }
      yield getUsersByRole(UserRole.portal);
    });

    const loadPortalStats = flow(function* () {
      try {
        self.portalStatsLoading = true;
        const response = yield fetchPortalUserStats();
        const raw = (response.data ?? {}) as unknown as Record<string, unknown>;
        const codeSubscriptions = coerceNumber(raw.codeSubscriptions) ?? defaultPortalStats.codeSubscriptions;
        const adminSubscriptions = coerceNumber(raw.adminSubscriptions) ?? defaultPortalStats.adminSubscriptions;
        const adminAddedSubscriptions =
          coerceNumber(raw.adminAddedSubscriptions) ?? defaultPortalStats.adminAddedSubscriptions;
        self.portalStats = {
          ...defaultPortalStats,
          ...(response.data ?? {}),
          codeSubscriptions,
          adminSubscriptions,
          adminAddedSubscriptions,
        };
        self.portalStatsLoading = false;
      } catch (err) {
        self.portalStatsLoading = false;
        const error = composeError(err);
        self.rootStore.errorStore.setError(error.message);
        console.error(err);
      }
    });

    const forcePortalSubscriptionExpiry = flow(function* (payload: ForceSubscriptionExpiryPayload) {
      try {
        yield forceSubscriptionExpiryRequest(payload);
        yield getUsersByRole(UserRole.portal);
        self.rootStore.errorStore.setSuccess('portal_users.force_expiry_success');
      } catch (err) {
        const error = composeError(err);
        self.rootStore.errorStore.setError(error.message);
        console.error(err);
        throw error;
      }
    });

    const startUsersRequest = (role: UserRole) => {
      const previousRole = self.latestUsersRole;
      if (previousRole && previousRole !== role) {
        self.users.clear();
      }
      self.latestUsersRole = role;
      self.usersRequestToken += 1;
      return self.usersRequestToken;
    };

    const isLatestUsersRequest = (role: UserRole, token: number) =>
      self.latestUsersRole === role && self.usersRequestToken === token;

    // Get users by role (admin (admin, publisher, creator), employee or user)
    const getUsersByRole = flow(function* (userRole: UserRole, filters?: Partial<PortalUserFilters>) {
      const requestToken = startUsersRequest(userRole);
      if (userRole === UserRole.portal) {
        try {
          if (filters) {
            setPortalFilters(filters);
          }
          const snapshot = getSnapshot(self.portalFilters);
          self.portalUsersLoading = true;
          const usersResponse = yield fetchUsers(userRole, snapshot as PortalUserFilters);
          if (!isLatestUsersRequest(userRole, requestToken)) {
            self.portalUsersLoading = false;
            return;
          }

          const payload = usersResponse.data as PortalUsersResponse | User[];
          const isLegacyArrayPayload = Array.isArray(payload);
          const requestedLimit = (snapshot as PortalUserFilters).limit ?? defaultPortalFilters.limit ?? 100;
          const requestedOffset = (snapshot as PortalUserFilters).offset ?? 0;
          const payloadRecord =
            (!isLegacyArrayPayload && payload ? (payload as unknown as Record<string, unknown>) : undefined) ?? {};
          const portalData = isLegacyArrayPayload
            ? payload
            : Array.isArray(payloadRecord.data)
              ? (payloadRecord.data as User[])
              : Array.isArray(payloadRecord.users)
                ? (payloadRecord.users as User[])
                : Array.isArray(payloadRecord.results)
                  ? (payloadRecord.results as User[])
                  : [];
          const pagination =
            (payloadRecord.pagination as Record<string, unknown> | undefined) ??
            ((payloadRecord.meta as Record<string, unknown> | undefined)?.pagination as
              | Record<string, unknown>
              | undefined) ??
            undefined;
          const normalizedPortalData = portalData.map((userRecord: User) => mapUserResponse(userRecord));

          const headers = (usersResponse?.headers ?? {}) as Record<string, unknown>;
          const paginationLimit =
            (pagination ? coerceNumber(pagination.limit) : undefined) ??
            (pagination ? coerceNumber((pagination as Record<string, unknown>).pageSize) : undefined) ??
            (pagination ? coerceNumber((pagination as Record<string, unknown>).perPage) : undefined) ??
            coerceNumber(payloadRecord.limit) ??
            coerceNumber(payloadRecord.pageSize);
          const paginationOffset =
            (pagination ? coerceNumber(pagination.offset) : undefined) ??
            (pagination ? coerceNumber((pagination as Record<string, unknown>).skip) : undefined) ??
            coerceNumber(payloadRecord.offset) ??
            coerceNumber(payloadRecord.skip);
          const paginationTotal =
            (pagination ? coerceNumber(pagination.total) : undefined) ??
            (pagination ? coerceNumber((pagination as Record<string, unknown>).totalCount) : undefined) ??
            (pagination ? coerceNumber((pagination as Record<string, unknown>).count) : undefined) ??
            (pagination ? coerceNumber((pagination as Record<string, unknown>).recordsTotal) : undefined) ??
            coerceNumber(payloadRecord.total) ??
            coerceNumber(payloadRecord.totalCount) ??
            coerceNumber((payloadRecord.meta as Record<string, unknown> | undefined)?.total) ??
            coerceNumber(headers['x-total-count']) ??
            coerceNumber(headers['x-pagination-total']);
          const paginationHasMore =
            (pagination ? coerceBoolean(pagination.hasMore) : undefined) ??
            (pagination ? coerceBoolean((pagination as Record<string, unknown>).has_more) : undefined) ??
            coerceBoolean(payloadRecord.hasMore) ??
            coerceBoolean(payloadRecord.has_more);

          const shouldLocalPage = isLegacyArrayPayload || normalizedPortalData.length > requestedLimit;
          const responseAlreadyOffsetApplied =
            shouldLocalPage &&
            requestedOffset > 0 &&
            ((typeof paginationOffset === 'number' && paginationOffset === requestedOffset) ||
              (typeof paginationTotal === 'number' &&
                normalizedPortalData.length <= Math.max(paginationTotal - requestedOffset, 0)));
          if (shouldLocalPage && process.env.NODE_ENV !== 'production' && !portalLocalPagingWarned) {
            portalLocalPagingWarned = true;
            console.warn('[portal-users] Backend returned more rows than requested; applying local paging fallback.', {
              requestedLimit,
              requestedOffset,
              received: normalizedPortalData.length,
              legacyArrayPayload: isLegacyArrayPayload,
              responseAlreadyOffsetApplied,
            });
          }
          const preferredSliceStart = responseAlreadyOffsetApplied ? 0 : requestedOffset;
          const localSliceStart =
            preferredSliceStart >= normalizedPortalData.length && requestedOffset > 0 ? 0 : preferredSliceStart;
          const effectivePortalData = shouldLocalPage
            ? normalizedPortalData.slice(localSliceStart, localSliceStart + requestedLimit)
            : normalizedPortalData;
          applySnapshot(self.users, effectivePortalData);

          const resolvedLimit = paginationLimit ?? requestedLimit;
          const resolvedOffset = paginationOffset ?? requestedOffset;
          const resolvedTotal = paginationTotal ?? (shouldLocalPage ? normalizedPortalData.length : 0);
          const resolvedHasMore = shouldLocalPage
            ? resolvedOffset + effectivePortalData.length < resolvedTotal
            : (paginationHasMore ?? resolvedOffset + effectivePortalData.length < resolvedTotal);

          self.portalPagination = {
            total: resolvedTotal,
            limit: resolvedLimit,
            offset: resolvedOffset,
            hasMore: resolvedHasMore,
          };
          self.portalUsersLoading = false;
        } catch (err) {
          if (isLatestUsersRequest(userRole, requestToken)) {
            self.portalUsersLoading = false;
          }
          const error = composeError(err);
          self.rootStore.errorStore.setError(error.message);
          console.error(err);
        }
        return;
      }

      try {
        const response = yield fetchUsers(userRole);
        if (isLatestUsersRequest(userRole, requestToken)) {
          self.users = response.data.map((r: User) => mapUserResponse(r));
        }
      } catch (err) {
        const error = composeError(err);
        self.rootStore.errorStore.setError(error.message);
        console.error(err);
      }
    });

    const getUserById = flow(function* (id: string) {
      try {
        const response = yield getSingleUser(id, { includeInactive: true });
        const user = response.data as User;
        const snapshot = mapSingleUserSnapshot(user);
        self.singleUser = UserModel.create(snapshot);
      } catch (err) {
        const error = composeError(err);
        self.rootStore.errorStore.setError(error.message);
        console.error(err);
      }
    });

    // TODO siirrä subscriptionStoreen
    const startStripeRequest = (userId: string) => {
      self.stripeSubscriptionsUserId = userId;
      self.stripeSubscriptionsLoading = true;
      self.stripeSubscriptionsRequestToken += 1;
      self.stripeSubscriptions.clear();
      return self.stripeSubscriptionsRequestToken;
    };

    const isLatestStripeRequest = (userId: string, token: number) =>
      self.stripeSubscriptionsUserId === userId && self.stripeSubscriptionsRequestToken === token;

    const getStripeSubscriptions = flow(function* (id: string) {
      const requestToken = startStripeRequest(id);
      try {
        const response = yield getUserStripeSubscriptions(id);
        if (isLatestStripeRequest(id, requestToken)) {
          self.stripeSubscriptions.replace(
            response.data?.map((s: StripeSubscription) => ({
              ...s,
            })) ?? []
          );
          self.stripeSubscriptionsLoading = false;
        }
      } catch (err) {
        const error = composeError(err);
        if (isLatestStripeRequest(id, requestToken)) {
          self.stripeSubscriptions.replace([]);
          self.stripeSubscriptionsLoading = false;
          self.rootStore.errorStore.setError(error.message);
        }
        console.error(err);
      }
    });

    // TODO siirrä subscriptionStoreen
    const getProducts = flow(function* () {
      try {
        const result = yield getSubscriptionProducts();

        self.products = result.data.map((product: ProductModelType) => ({
          ...product,
        }));
      } catch (error) {
        console.error(error);
      }
    });

    const createUser = flow(function* (newUser) {
      try {
        yield createSingleUser(newUser);
        self.rootStore.errorStore.setSuccess('save_success');
        getUsersByRole(getUserListRole(newUser));
      } catch (err: unknown) {
        const error = composeError(err);
        self.rootStore.errorStore.setError(error.message);
        console.error(err);
      }
    });

    const updateUser = flow(function* (id, user) {
      try {
        yield updateSingleUser(id, user);
        // Keep singleUser in sync after update
        const refreshed = yield getSingleUser(String(id));
        const refreshedUser = mapSingleUserSnapshot(refreshed.data as User);
        self.singleUser = UserModel.create(refreshedUser);
        self.rootStore.errorStore.setSuccess('save_success');
        getUsersByRole(getUserListRole(user));
      } catch (err) {
        const error = composeError(err);
        self.rootStore.errorStore.setError(error.message);
        console.error(err);
      }
    });

    const deleteUser = flow(function* (id: number, userRole: UserRole) {
      try {
        yield deleteSingleUser(id);
        self.rootStore.errorStore.setSuccess('delete_success');
        getUsersByRole(userRole);
      } catch (err) {
        const error = composeError(err);
        self.rootStore.errorStore.setError(error.message);
        console.error(err);
      }
    });

    const changePassword = flow(function* (password: string) {
      try {
        yield changeUserPassword(password);
        self.rootStore.errorStore.setSuccess('password_changed');
      } catch (err) {
        const error = composeError(err);
        self.rootStore.errorStore.setError(error.message);
        console.error(err);
      }
    });

    const uploadUsers = flow(function* (file) {
      try {
        yield uploadUsersFromFile(file);
        self.rootStore.errorStore.setSuccess('save_success');
        getUsersByRole(UserRole.employee);
      } catch (err) {
        const error = composeError(err);
        self.rootStore.errorStore.setError(error.message);
        console.error(err);
      }
    });

    const hasCommunityAccess = (communityId: number) => {
      try {
        if (self.user?.admin) {
          return true;
        }

        if (!self.user) {
          return false;
        }

        const exists = self.user.communities?.some((c) => c.id == communityId);
        return exists;
      } catch (err) {
        const error = composeError(err);
        self.rootStore.errorStore.setError(error.message);
        console.error(err);
      }
    };

    const getCommunities = flow(function* () {
      try {
        const response = yield fetchCommunities();
        self.communities = response.data.map((r: Community) => ({ ...r, name: r.community }));
      } catch (err) {
        const error = composeError(err);
        self.rootStore.errorStore.setError(error.message);
        console.error(err);
      }
    });

    const getCommunityById = flow(function* (id: string) {
      try {
        const response = yield getSingleCommunity(id);
        return response.data;
      } catch (err) {
        const error = composeError(err);
        self.rootStore.errorStore.setError(error.message);
        console.error(err);
      }
    });

    const createCommunity = flow(function* (fields: Community) {
      try {
        yield createSingleCommunity(fields);
        self.rootStore.errorStore.setSuccess('save_success');
        getCommunities();
      } catch (err) {
        const error = composeError(err);
        self.rootStore.errorStore.setError(error.message);
        console.error(err);
      }
    });

    const updateCommunity = flow(function* (id: string, fields: Community) {
      try {
        yield updateSingleCommunity(id, fields);
        self.rootStore.errorStore.setSuccess('save_success');
        getCommunities();
      } catch (err) {
        const error = composeError(err);
        self.rootStore.errorStore.setError(error.message);
        console.error(err);
      }
    });

    const deleteCommunity = flow(function* (id: string) {
      try {
        yield deleteSingleCommunity(id);
        self.rootStore.errorStore.setSuccess('delete_success');
        getCommunities();
      } catch (err) {
        const error = composeError(err);
        self.rootStore.errorStore.setError(error.message);
        console.error(err);
      }
    });

    const changePasswordWithToken = flow(function* (password: string, resetToken: string) {
      try {
        yield resetUserPassword(password, resetToken);
        self.rootStore.errorStore.setSuccess('password_changed');
      } catch (err) {
        const error = composeError(err);
        self.rootStore.errorStore.setError(error.message);
        console.error(err);
      }
    });

    const sendPasswordResetLink = flow(function* (email: string) {
      try {
        yield forgotUserPassword(email);
        self.rootStore.errorStore.setSuccess('password_reset_link_sent');
      } catch (err) {
        const error = composeError(err);
        self.rootStore.errorStore.setError(error.message);
        console.error(err);
      }
    });

    const clearSingleUser = () => {
      self.singleUser = null;
    };

    const clearStripeSubscriptions = () => {
      self.stripeSubscriptions.clear();
      self.stripeSubscriptionsUserId = null;
      self.stripeSubscriptionsLoading = false;
    };

    return {
      userLogin,
      userLogout,
      getMe,
      getUsersByRole,
      loadPortalUsersWithFilters,
      resetPortalFilters,
      loadPortalStats,
      forcePortalSubscriptionExpiry,
      getUserById,
      createUser,
      updateUser,
      deleteUser,
      changePassword,
      uploadUsers,
      hasCommunityAccess,
      getCommunities,
      createCommunity,
      updateCommunity,
      deleteCommunity,
      getCommunityById,
      changePasswordWithToken,
      sendPasswordResetLink,
      getStripeSubscriptions,
      getProducts,
      clearSingleUser,
      clearStripeSubscriptions,
    };
  });

export const UserModelHydration = types.model('UserModelHydration', {
  id: types.maybeNull(types.number),
  // ... existing code ...
});
